'use client';

// Recovered from the IMAI v5 animation source; demo documents stay illustrative.
import * as React from 'react';
import { ShowcaseCopyContext, translateShowcaseNode } from '../translation';
import { ef, J } from './sell-market-animation';
import { referenceProducts } from './reference-products';
import {
  up,
  dp,
  fp,
  LockIllustration,
  FreezeDecision,
  rp,
} from './factory-handoff';
import {
  ProductionArtifact,
  ProductionIllustrativeBadge,
  ProductionBadge,
} from './production-primitives';
import { ShowcaseIcon, ProductRender } from './scene-primitives';
import { referenceImages } from './reference-brands';
import { ProductionIcon } from './deliverable-previews';
import { ShowcaseSpinner } from './playback-controls';
export function CollectionFreezePacket({ t: e }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let t = ef(e, 500, 140, referenceProducts.length),
    n = ef(e, 1900, 320, up.length),
    r = ef(e, 3100, 320, dp.length),
    i = e >= fp;
  return (
    <ProductionArtifact
      icon={'lock'}
      title={translateShowcaseNode(showcaseCopy, 'FW27 Freeze Packet')}
      view={'Document'}
      footer={
        <React.Fragment>
          <span
            className={'text-ink-3 flex min-w-0 items-center gap-2 text-[13px]'}
          >
            <ShowcaseIcon name={'doc'} className={'size-4 shrink-0'} />
            <span className={'truncate font-mono text-[12.5px]'}>
              {translateShowcaseNode(
                showcaseCopy,
                'lisiere-fw27-freeze-packet.pdf'
              )}
            </span>
          </span>
          <ProductionIllustrativeBadge className={'ml-auto'} />
        </React.Fragment>
      }
    >
      <div
        className={
          'grid gap-5 p-4 sm:p-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] md:gap-7'
        }
      >
        <div className={'flex min-w-0 flex-col gap-4'}>
          <div className={'flex items-start gap-3'}>
            <div className={'min-w-0 flex-1'}>
              <p className={'label'}>
                {translateShowcaseNode(
                  showcaseCopy,
                  'LISI\xC8RE \xB7 FW27 capsule'
                )}
              </p>
              <p
                className={
                  'text-ink mt-1 text-[20px] leading-7 font-semibold tracking-[-0.015em]'
                }
              >
                {translateShowcaseNode(showcaseCopy, 'Freeze packet')}
              </p>
              <p className={'num text-ink-3 mt-0.5 text-[13px]'}>
                {translateShowcaseNode(
                  showcaseCopy,
                  '6 products \xB7 frozen at collection v4'
                )}
              </p>
            </div>
            <span
              className={
                'relative grid size-14 shrink-0 place-items-center rounded-[14px] bg-[var(--tint)]'
              }
            >
              <LockIllustration t={e} />
            </span>
          </div>
          <FreezeDecision
            title={translateShowcaseNode(showcaseCopy, 'Versions')}
            count={'6'}
            t={e}
            start={400}
          >
            <ul className={'flex flex-col'}>
              {translateShowcaseNode(
                showcaseCopy,
                referenceProducts.map((e, n) => {
                  let r = referenceImages.lisiere.pieces[n];
                  return (
                    <li
                      key={e.name}
                      data-on={String(t > n)}
                      className={
                        'pd-in flex h-9 items-center gap-2.5 border-b border-[var(--line)] text-[13px] last:border-0'
                      }
                    >
                      {translateShowcaseNode(
                        showcaseCopy,
                        r ? (
                          <ProductRender
                            render={r}
                            alt={translateShowcaseNode(showcaseCopy, '')}
                            className={'size-6 shrink-0 rounded-[5px]'}
                          />
                        ) : null
                      )}
                      <span className={'text-ink min-w-0 flex-1 truncate'}>
                        {translateShowcaseNode(showcaseCopy, e.style)}
                      </span>
                      <span className={'num text-ink-3 font-mono text-[12px]'}>
                        {translateShowcaseNode(showcaseCopy, rp[n])}
                      </span>
                      <span
                        className={`grid size-4 place-items-center text-[var(--pd-green)] transition-opacity duration-300 ${i ? 'opacity-100' : 'opacity-0'}`}
                      >
                        <ShowcaseIcon
                          name={'check'}
                          className={'size-3.5'}
                          strokeWidth={2}
                        />
                      </span>
                    </li>
                  );
                })
              )}
            </ul>
          </FreezeDecision>
        </div>
        <div className={'flex min-w-0 flex-col gap-5'}>
          <FreezeDecision
            title={translateShowcaseNode(showcaseCopy, 'Accepted decisions')}
            count={'3'}
            t={e}
            start={1800}
          >
            <ol className={'flex flex-col gap-2'}>
              {translateShowcaseNode(
                showcaseCopy,
                up.map((e, t) => (
                  <li
                    key={e}
                    data-on={String(n > t)}
                    className={'pd-in flex gap-2.5 text-[13.5px] leading-[1.5]'}
                  >
                    <span
                      className={
                        'num text-ink-4 w-4 shrink-0 font-mono text-[12px] leading-[20px]'
                      }
                    >
                      {translateShowcaseNode(showcaseCopy, t + 1)}
                    </span>
                    <span className={'text-ink-2'}>
                      {translateShowcaseNode(showcaseCopy, e)}
                    </span>
                  </li>
                ))
              )}
            </ol>
          </FreezeDecision>
          <FreezeDecision
            title={translateShowcaseNode(showcaseCopy, 'Open conditions')}
            count={'2'}
            t={e}
            start={3e3}
          >
            <ul className={'flex flex-col gap-2'}>
              {translateShowcaseNode(
                showcaseCopy,
                dp.map((e, t) => (
                  <li
                    key={e}
                    data-on={String(r > t)}
                    className={
                      'pd-pop flex gap-2.5 rounded-[10px] bg-[var(--pd-amber-wash)] p-2.5 text-[13px] leading-[1.45]'
                    }
                  >
                    <span className={'mt-0.5 text-[var(--pd-amber)]'}>
                      <ProductionIcon
                        name={'circleAlert'}
                        className={'size-4'}
                      />
                    </span>
                    <span className={'text-ink-2'}>
                      {translateShowcaseNode(showcaseCopy, e)}
                    </span>
                  </li>
                ))
              )}
            </ul>
          </FreezeDecision>
          <div
            {...J(e, fp)}
            className={'pd-pop mt-auto flex items-center gap-2'}
          >
            <ProductionBadge tone={'neutral'} className={'text-ink'}>
              <ShowcaseIcon name={'lock'} className={'size-3.5'} />
              {translateShowcaseNode(showcaseCopy, 'Frozen at v4')}
            </ProductionBadge>
            <span className={'text-ink-3 text-[12.5px]'}>
              {translateShowcaseNode(
                showcaseCopy,
                'Conditions travel with the packet.'
              )}
            </span>
          </div>
        </div>
      </div>
    </ProductionArtifact>
  );
}
export const gp = [
  {
    name: 'Tech pack',
    note: '4 spec conflicts resolved, v2 approved',
    ok: !0,
  },
  {
    name: 'Materials',
    note: 'Leather article awaiting approval',
    ok: !1,
  },
  {
    name: 'Costing',
    note: '\u20AC99,273 base envelope within ~\u20AC100,000',
    ok: !0,
  },
  {
    name: 'Sample',
    note: 'Handle fix not yet seen on a sample',
    ok: !1,
  },
  {
    name: 'Colour',
    note: 'Cognac edge paint approved',
    ok: !0,
  },
  {
    name: 'Packaging',
    note: 'Dust bag and care card specified',
    ok: !0,
  },
];
export const _p = 400;
export const vp = 520;
export const yp = _p + gp.length * vp + 300;
export const bp = ['Go', 'Conditional Go', 'Hold'];
export const xp = 1;
export const Sp = [
  'Approve the leather article before cutting.',
  'Confirm the 24 cm handle drop on the pre-production sample.',
];
export function ReadinessGate({ gate: e, index: t, t: n }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let r = _p + t * vp,
    i = n >= r + 380;
  return (
    <li
      {...J(n, r)}
      className={
        'pd-in flex min-h-11 items-center gap-3 border-b border-[var(--line)] py-2 last:border-0'
      }
    >
      <span
        className={`grid size-5 shrink-0 place-items-center ${e.ok ? 'text-[var(--pd-green)]' : 'text-[var(--pd-amber)]'}`}
      >
        {translateShowcaseNode(
          showcaseCopy,
          i ? (
            <ProductionIcon
              name={e.ok ? 'circleCheck' : 'circleAlert'}
              on={i}
              className={'size-[18px]'}
            />
          ) : (
            <ShowcaseSpinner className={'text-ink-3 size-4'} />
          )
        )}
      </span>
      <span
        className={
          'flex min-w-0 flex-1 flex-col sm:flex-row sm:items-center sm:gap-3'
        }
      >
        <span className={'text-ink w-24 shrink-0 text-[13.5px] font-medium'}>
          {translateShowcaseNode(showcaseCopy, e.name)}
        </span>
        <span className={'text-ink-3 min-w-0 truncate text-[13px]'}>
          {translateShowcaseNode(showcaseCopy, e.note)}
        </span>
      </span>
    </li>
  );
}
